import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import PurchaseOrderHeaderFields, {
  PurchaseOrderHeaderData,
} from "../PurchaseOrderHeaderFields";
import { purchaseOrderService } from "../../services/purchaseOrder.service";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (poId: number) => void;
}

const CreatePurchaseOrderModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [headerData, setHeaderData] = useState<PurchaseOrderHeaderData>({
    poNumber: "",
    clientID: 0,
    clientName: "",
    subClientId: undefined,
    subClientName: "",
    poDate: new Date().toISOString().split("T")[0],
    comment: "",
    statusID: 5,
    priorityID: 2,
  });

  const handleClose = () => {
    if (!loading) {
      setHeaderData({
        poNumber: "",
        clientID: 0,
        clientName: "",
        subClientId: undefined,
        subClientName: "",
        poDate: new Date().toISOString().split("T")[0],
        comment: "",
        statusID: 5,
        priorityID: 2,
      });
      setError("");
      onClose();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      if (!headerData.poNumber || !headerData.clientName) {
        throw new Error("Please fill in all required fields");
      }

      const result = await purchaseOrderService.createPurchaseOrder({
        toEntityID: 2019,
        clientID: headerData.clientID,
        subClientId: headerData.subClientId,
        poNumber: headerData.poNumber,
        poDate: headerData.poDate,
        clientName: headerData.clientName,
        subClientName: headerData.subClientName,
        comment: headerData.comment || "",
        statusID: headerData.statusID || 5,
        priorityID: headerData.priorityID || 2,
        signatureRequired: headerData.signatureRequired || false,
        fromInventory: headerData.fromInventory || false,
        details: [],
      });

      onSuccess(result.id);
      handleClose();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to create purchase order",
      );
    } finally {
      setLoading(false);
    }
  };

  const isValid = headerData.poNumber.trim() !== "" && headerData.clientID > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Create Purchase Order</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <PurchaseOrderHeaderFields
          data={headerData}
          onChange={setHeaderData}
          disabled={loading}
          showNextPoNumber={true}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={!isValid || loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? "Creating..." : "Create PO"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePurchaseOrderModal;
